import React, { useEffect, useState, useRef } from 'react';
import { users } from '@/lib/kbApi';
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Mail, MessageSquare, Calendar, MapPin, Building } from "lucide-react";

interface UserProfile {
  id: string;
  name: string;
  email: string;
  role?: string;
  department?: string;
  location?: string;
  joinDate?: string;
  lastSeen?: string;
  articlesCount?: number;
}

interface ReadViewMentionHoverProps {
  containerRef: React.RefObject<HTMLElement>;
}

export function ReadViewMentionHover({ containerRef }: ReadViewMentionHoverProps) {
  const [hoveredMention, setHoveredMention] = useState<{
    email: string;
    element: HTMLElement;
    rect: DOMRect;
  } | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout>();
  const hideTimeoutRef = useRef<NodeJS.Timeout>();

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleMouseEnter = (event: Event) => {
      const target = event.target as HTMLElement;

      if ((window as any).__DEBUG_MENTIONS__) {
        console.log('🔍 [READ] mention hover detected');
        console.log('🔍 [READ] element outerHTML:', target.outerHTML);
        console.log('🔍 [READ] boundingClientRect:', target.getBoundingClientRect());

        // Walk up DOM 5 levels and check computed styles
        let element: Element | null = target;
        for (let i = 0; i < 5 && element; i++) {
          const styles = window.getComputedStyle(element);
          console.log(`🔍 [READ] parent level ${i}:`, {
            tagName: element.tagName,
            className: element.className,
            zIndex: styles.zIndex,
            position: styles.position,
            overflow: styles.overflow,
          });
          element = element.parentElement;
        }
      }
      console.log('🔍 [READ HOVER DEBUG] Mouse enter on element:', target);
      console.log('🔍 [READ HOVER DEBUG] Element classes:', target.className);
      console.log('🔍 [READ HOVER DEBUG] Element attributes:', target.outerHTML);

      if (!target.matches('.mention[data-user-email]')) {
        console.log('🔍 [READ HOVER DEBUG] Element does not match .mention[data-user-email]');
        return;
      }

      const email = target.getAttribute('data-user-email');
      console.log('🔍 [READ HOVER DEBUG] Found email attribute:', email);
      if (!email) return;

      // Clear any pending hide timeout
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = undefined;
      }

      // Set hover timeout to show after 300ms
      hoverTimeoutRef.current = setTimeout(() => {
        const rect = target.getBoundingClientRect();
        console.log('🔍 [READ HOVER DEBUG] Setting hovered mention:', { email, rect });
        setHoveredMention({
          email,
          element: target,
          rect
        });
      }, 300);
    };

    const handleMouseLeave = (event: Event) => {
      const target = event.target as HTMLElement;
      if (!target.matches('.mention[data-user-email]')) return;

      // Clear show timeout
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = undefined;
      }

      // Set hide timeout
      hideTimeoutRef.current = setTimeout(() => {
        setHoveredMention(null);
        setUserProfile(null);
      }, 100);
    };

    // Use event delegation
    container.addEventListener('mouseenter', handleMouseEnter, true);
    container.addEventListener('mouseleave', handleMouseLeave, true);

    return () => {
      container.removeEventListener('mouseenter', handleMouseEnter, true);
      container.removeEventListener('mouseleave', handleMouseLeave, true);
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [containerRef]);

  // Fetch user profile when mention is hovered
  useEffect(() => {
    if (!hoveredMention) {
      setUserProfile(null);
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    const fetchUserProfile = async () => {
      try {
        const response = await users.getByEmail(hoveredMention.email);
        if (mounted && response.user) {
          setUserProfile(response.user);
        }
      } catch (err) {
        if (mounted) {
          console.warn('Could not fetch user profile for', hoveredMention.email, err);
          // Set a basic profile from available data
          setUserProfile({
            id: hoveredMention.email,
            name: hoveredMention.email.split('@')[0],
            email: hoveredMention.email,
          });
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchUserProfile();

    return () => {
      mounted = false;
    };
  }, [hoveredMention]);

  const getUserInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleSendMessage = () => {
    if (userProfile?.email) {
      navigator.clipboard.writeText(userProfile.email);
    }
  };

  if (!hoveredMention || !userProfile) {
    return null;
  }

  // Position the hover card above the mention element
  const top = hoveredMention.rect.top + window.scrollY - 10;
  const left = hoveredMention.rect.left + window.scrollX;

  return (
    <div
      className="fixed z-50 bg-background border border-border rounded-lg shadow-lg p-4 w-80"
      style={{
        top: `${top - 200}px`, // Position above the element
        left: `${Math.max(10, Math.min(left, window.innerWidth - 330))}px`, // Keep within viewport
        pointerEvents: 'auto',
      }}
      onMouseEnter={() => {
        // Keep card open when hovering over it
        if (hideTimeoutRef.current) {
          clearTimeout(hideTimeoutRef.current);
          hideTimeoutRef.current = undefined;
        }
      }}
      onMouseLeave={() => {
        // Hide when leaving the card
        hideTimeoutRef.current = setTimeout(() => {
          setHoveredMention(null);
          setUserProfile(null);
        }, 100);
      }}
    >
      {loading ? (
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-muted rounded-full animate-pulse" />
          <div className="space-y-2 flex-1">
            <div className="h-4 bg-muted rounded animate-pulse" />
            <div className="h-3 bg-muted rounded w-2/3 animate-pulse" />
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center space-x-3">
            <Avatar className="w-12 h-12">
              <AvatarFallback className="bg-primary/10 text-primary font-medium">
                {getUserInitials(userProfile.name)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-sm truncate">
                {userProfile.name}
              </h4>
              <div className="flex items-center text-xs text-muted-foreground">
                <Mail className="w-3 h-3 mr-1" />
                <span className="truncate">{userProfile.email}</span>
              </div>
            </div>
          </div>

          {/* Badges */}
          <div className="flex gap-2 flex-wrap">
            {userProfile.role && (
              <Badge variant="secondary" className="text-xs">
                {userProfile.role}
              </Badge>
            )}
            {userProfile.department && (
              <Badge variant="outline" className="text-xs">
                <Building className="w-3 h-3 mr-1" />
                {userProfile.department}
              </Badge>
            )}
          </div>

          {/* Details */}
          <div className="space-y-2 text-xs text-muted-foreground">
            {userProfile.location && (
              <div className="flex items-center">
                <MapPin className="w-3 h-3 mr-2" />
                {userProfile.location}
              </div>
            )}
            {userProfile.joinDate && (
              <div className="flex items-center">
                <Calendar className="w-3 h-3 mr-2" />
                Joined {new Date(userProfile.joinDate).toLocaleDateString()}
              </div>
            )}
            {userProfile.articlesCount !== undefined && (
              <div className="flex items-center">
                <span className="w-3 h-3 mr-2 text-center">📝</span>
                {userProfile.articlesCount} articles authored
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleSendMessage}
              className="flex-1 text-xs h-8"
            >
              <MessageSquare className="w-3 h-3 mr-1" />
              Contact
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => window.open(`mailto:${userProfile.email}`, '_blank')}
              className="text-xs h-8"
            >
              <Mail className="w-3 h-3" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}